import { withLayouts } from 'react-dx'
import { Trans, useLingui } from '@lingui/react/macro'

import AntdLayout from './_layout/layouts/AntdLayout'
import SiteSpecificContextLayout from './_layout/layouts/SiteSpecificContextLayout'
import { TopBanner } from './_blocks/IndexPage/TopBanner'
import { CreateCustom } from './_blocks/IndexPage/CreateCustom'
import { SafeFrom } from './_blocks/IndexPage/SafeFrom'
import { VariousGenres } from './_blocks/IndexPage/VariousGenres'
import { WhoIs } from './_blocks/IndexPage/WhoIs'
import { HowTo } from './_blocks/IndexPage/HowTo'
import { Cta } from './_blocks/IndexPage/Cta'
import { Faq } from './_components/Faq'
import { AiThinking } from './_blocks/IndexPage/AiThinking'
import { ProPrompts } from './_blocks/IndexPage/ProPrompts'

import type { InferGetServerSidePropsType } from 'next'
import type { TNextPagePro } from '@/types/global'

import DefaultLayout from '@/pages/tad.ai/_layout'
import RootLayout from '@/pages/tad.ai/_layout/layouts/RootLayout'
import LazyImage from '@/components/LazyImage'
import { CreateDownload } from '@/pages/tad.ai/_blocks/IndexPage/CreateDownload'
import { useLocale } from '@/hooks/routes'
import RatingStructured, {
  generateRatingData,
  getCurrencyByLocale,
} from '@/components/Structured/RatingStructured'


import { useCanonicalHref } from '@/hooks/routes';
import Link from '@/components/Link';
import { getServerSideData } from '@/helpers/server-data';
const sectionSpacingClass = 'pt-20 md:pt-[120px]'

const Page: TNextPagePro<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = (props) => {
  const { ratingData } = props
  const [canonicalUrl] = useCanonicalHref()
  const { t } = useLingui()

  const locale = useLocale()

  // const textOrLink =
  //   locale === 'en' ? (
  //     <Link
  //       href='/hub/best-ai-music-generators'
  //       className='text-f-primary hover:underline'
  //     >
  //       {t`best AI music generators`}
  //     </Link>
  //   ) : (
  //     t`best AI music generators`
  //   )

  return (
    <>
      <RatingStructured
        name={t`Tad AI: Free AI Music Generator & Song Maker`}
        description={t`Discover Tad AI music generator and create stunning AI music. Enjoy the freedom to compose your own AI songs for free using advanced tools designed for all music lovers.`}
        origin={canonicalUrl}
        ratingValue={ratingData.ratingValue}
        reviewCount={ratingData.reviewCount}
        priceCurrency={ratingData.priceCurrency}
      />
      <div className='relative'>
        <LazyImage
          fill
          src={'/images/index/top-bg.jpg'}
          alt={t`Music Creation Made Easy`}
          className='max-h-[1150px]'
        />
        <TopBanner />
        {/* <Ideas /> */}
        <div className={'pt-8 md:pt-[30px]'}>
          <AiThinking />
        </div>
        <div className={sectionSpacingClass}>
          <ProPrompts />
        </div>
        <div className={sectionSpacingClass}>
          <CreateCustom />
        </div>
      </div>
      <div className={sectionSpacingClass}>
        <SafeFrom />
      </div>
      <div className={sectionSpacingClass}>
        <VariousGenres />
      </div>
      <div className={sectionSpacingClass}>
        <WhoIs />
      </div>
      <div className={sectionSpacingClass}>
        <HowTo />
      </div>
      <div className={sectionSpacingClass}>
        <CreateDownload />
      </div>
      <div className='relative'>
        <LazyImage fill src={'/images/index/bottom-bg.jpg'} alt={'bottom'} />
        <Faq
          className={`relative z-[1] ${sectionSpacingClass}`}
          faqList={[
            {
              title: <Trans>What is AI music generator?</Trans>,
              desc: (
                <Trans>
                  An AI music generator leverages artificial intelligence to
                  craft unique, customized music compositions based on user
                  preferences and input. It offers a creative and accessible way
                  for individuals to produce original music for personal or
                  professional use.
                </Trans>
              ),
            },
            {
              title: <Trans>What is Tad AI?</Trans>,
              desc: (
                <Trans>
                  Tad AI is a cutting-edge AI song generator that helps you
                  compose original songs or instrumentals fast and easily.
                  Supported by powerful AI music models, Tad AI possesses an
                  advanced capability in generating high-quality music in
                  different styles, making it an invaluable resource.
                </Trans>
              ),
            },
            {
              title: <Trans>How does an AI song generator work?</Trans>,
              desc: (
                <Trans>
                  AI song makers like Tad AI operate through machine learning
                  algorithms that have been trained on vast datasets of existing
                  music. When you input guidance such as ideas, styles or
                  lyrics, the AI music generators analyze these parameters and
                  create a composition according to your needs.
                </Trans>
              ),
            },
            {
              title: <Trans>What is the best AI music generator?</Trans>,
              desc: (
                <>
                  {locale === 'en' ? (
                    <p>
                      <Trans>
                        Tad.ai is considered one of the{' '}
                        <Link
                          href='/hub/best-ai-music-generators'
                          className='text-f-primary hover:underline'
                        >
                          best AI music generators
                        </Link>{' '}
                        available today. With Tad AI, you can input a song title
                        and even generate lyrics, making it an excellent tool
                        for musicians and content creators alike.
                      </Trans>
                    </p>
                  ) : (
                    <p>
                      <Trans>
                        Tad.ai is considered one of the best AI music generators
                        available today. With Tad AI, you can input a song title
                        and even generate lyrics, making it an excellent tool
                        for musicians and content creators alike.
                      </Trans>
                    </p>
                  )}
                  <p>
                    <Trans>
                      The AI song generator offers flexibility in selecting
                      genres and moods, enabling users to tailor their music to
                      fit specific projects or personal preferences.{' '}
                    </Trans>
                  </p>
                  <p>
                    <Trans>
                      Additionally, Tad.ai provides high-quality, royalty-free
                      music, which is perfect for various applications, from
                      videos to advertisements.
                    </Trans>
                  </p>
                </>
              ),
            },
            {
              title: <Trans>Is Tad AI free to use?</Trans>,
              desc: (
                <Trans>
                  Yes, Tad AI offers a basic plan that allows you to generate AI
                  music without any cost and provides free credits for new
                  users. If you want more credits and commercial use rights, you
                  can upgrade to one of our paid plans.
                </Trans>
              ),
            },
            {
              title: <Trans>Why do people use AI songs?</Trans>,
              desc: (
                <Trans>
                  People use AI music for various reasons, including the ability
                  to quickly generate original tracks, explore new musical
                  ideas, and enhance creative projects without needing extensive
                  musical training. AI music generators are particularly popular
                  among content creators, advertisers, and hobbyists who seek to
                  produce unique soundscapes for videos, presentations, and
                  personal projects.
                </Trans>
              ),
            },
            {
              title: (
                <Trans>
                  Can I use the AI music generated by this song maker for
                  commercial purposes?
                </Trans>
              ),
              desc: (
                <Trans>
                  Sure, you can use the AI songs for commercial projects if you
                  are on a paid plan with Tad AI. However, if you create music
                  under our basic plan, it is strictly for non-commercial uses.
                </Trans>
              ),
            },
            {
              title: (
                <Trans>
                  What type of music can Tad AI song generator create?
                </Trans>
              ),
              desc: (
                <Trans>
                  Tad AI can create AI music across a wide range of styles,
                  catering to diverse needs. You can generate tracks in genres
                  such as pop, rap, rock and much more. Additionally, our
                  platform supports customization like vocal, music weight and
                  others.
                </Trans>
              ),
            },
          ]}
        />
        <div className={sectionSpacingClass}>
          <Cta />
        </div>
      </div>
    </>
  )
}

const LayoutsPage = withLayouts(Page, [
  (props) => {
    return (
      <DefaultLayout
        {...props}
        main={{ className: 'bg-black', includingHeader: true }}
        header={{ transparentOnScrollTop0: true }}
      />
    )
  },
  (props) => {
    const { t } = useLingui()

    return (
      <RootLayout
        {...props}
        seo={{
          title: t`Tad AI: Free AI Music Generator & Song Maker`,
          description: t`Discover Tad AI music generator and create stunning AI music. Enjoy the freedom to compose your own AI songs for free using advanced tools designed for all music lovers.`,
        }}
      />
    )
  },
  (props) => {
    return <SiteSpecificContextLayout {...props} />
  },
  AntdLayout,
])

export default LayoutsPage

// eslint-disable-next-line react-refresh/only-export-components
export const getServerSideProps = getServerSideData({ cacheControl: true })((
  hostInfo,
  ctx,
) => {
  const priceCurrency = getCurrencyByLocale(ctx?.locale || 'en')
  const url = hostInfo.normalizeUrl().url.pathname
  const ratingData = generateRatingData(priceCurrency, url)
  return {
    ratingData,
  }
})
